import { Table, Typography, Stack, IconButton, Button, Chip, Divider, Tooltip } from '@mui/joy'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import LogoutIcon from '@mui/icons-material/Logout'
import ReplayIcon from '@mui/icons-material/Replay'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'

import { type TelegramAccount, type WatchedChat } from '../types'

interface AccountsTableProps {
    accounts: TelegramAccount[]
    watchedMap: Record<string, WatchedChat[]>
    loggingOutPhone: string | null
    watcherActionPhone: string | null
    onLogout: (phone: string) => void
    onReconnect: (phone: string) => void
    onManageWatch: (phone: string) => void
    onStartWatcher: (phone: string) => void
    onStopWatcher: (phone: string) => void
}

export function AccountsTable({
    accounts,
    watchedMap,
    loggingOutPhone,
    watcherActionPhone,
    onLogout,
    onReconnect,
    onManageWatch,
    onStartWatcher,
    onStopWatcher,
}: AccountsTableProps): JSX.Element {
    if (accounts.length === 0) {
        return (
            <Table variant="outlined" size="sm" sx={{ tableLayout: 'fixed', '& thead th': { bgcolor: 'neutral.softBg' } }}>
                <thead>
                    <tr>
                        <th style={{ width: 200 }}>Phone</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th style={{ width: 240 }}>Watching</th>
                        <th style={{ width: 400 }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td colSpan={5}>
                            <Typography level="body-sm" sx={{ p: 2, textAlign: 'center' }}>
                                No accounts yet. Click the <b>+</b> button to add one.
                            </Typography>
                        </td>
                    </tr>
                </tbody>
            </Table>
        )
    }

    return (
        <Table
            variant="outlined"
            size="sm"
            sx={{
                tableLayout: 'fixed',
                '& thead th': { bgcolor: 'neutral.softBg' },
            }}
        >
            <thead>
                <tr>
                    <th style={{ width: 200 }}>Phone</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th style={{ width: 240 }}>Watching</th>
                    <th style={{ width: 400 }}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {accounts.map((account) => {
                    const watched = watchedMap[account.phone] || []
                    return (
                        <tr key={account.id}>
                            <td>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <PhoneIphoneIcon fontSize="small" />
                                    <Typography level="body-md">{account.phone}</Typography>
                                </Stack>
                            </td>
                            <td>
                                <Chip
                                    size="sm"
                                    variant="soft"
                                    color={
                                        account.status === 'active'
                                            ? 'success'
                                            : account.status === 'logged_out'
                                              ? 'warning'
                                              : account.status === 'inactive'
                                                ? 'neutral'
                                                : 'neutral'
                                    }
                                >
                                    {account.status}
                                </Chip>
                            </td>
                            <td>
                                <Typography level="body-sm">{new Date(account.createdAt).toLocaleString()}</Typography>
                            </td>
                            <td>
                                {account.status === 'active' ? (
                                    watched.length > 0 ? (
                                        <Chip size="sm" variant="soft" color="success" startDecorator="✓">
                                            {watched.length} watching
                                        </Chip>
                                    ) : (
                                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                            None
                                        </Typography>
                                    )
                                ) : account.status === 'inactive' ? (
                                    <Chip size="sm" variant="soft" color="neutral">
                                        {watched.length > 0 ? `${watched.length} paused` : 'None'}
                                    </Chip>
                                ) : (
                                    <Chip size="sm" variant="soft" color="neutral" disabled>
                                        {watched.length > 0 ? `${watched.length} paused` : 'None'}
                                    </Chip>
                                )}
                            </td>
                            <td>
                                <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                                    {account.status === 'logged_out' ? (
                                        <Tooltip title="Reconnect account">
                                            <Button
                                                size="sm"
                                                variant="soft"
                                                color="primary"
                                                startDecorator={<ReplayIcon />}
                                                onClick={() => onReconnect(account.phone)}
                                            >
                                                Reconnect
                                            </Button>
                                        </Tooltip>
                                    ) : (
                                        <>
                                            <Tooltip title="Manage watched groups">
                                                <Button size="sm" variant="soft" startDecorator={<GroupAddIcon />} onClick={() => onManageWatch(account.phone)}>
                                                    Manage
                                                </Button>
                                            </Tooltip>
                                            <Divider orientation="vertical" />
                                            {account.status === 'active' ? (
                                                <Tooltip title="Stop watcher">
                                                    <Button
                                                        size="sm"
                                                        variant="soft"
                                                        color="danger"
                                                        startDecorator={<StopIcon />}
                                                        onClick={() => onStopWatcher(account.phone)}
                                                        loading={watcherActionPhone === account.phone}
                                                        disabled={watcherActionPhone === account.phone}
                                                    >
                                                        Stop
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Start watcher">
                                                    <Button
                                                        size="sm"
                                                        variant="soft"
                                                        color="success"
                                                        startDecorator={<PlayArrowIcon />}
                                                        onClick={() => onStartWatcher(account.phone)}
                                                        loading={watcherActionPhone === account.phone}
                                                        disabled={watcherActionPhone === account.phone}
                                                    >
                                                        Start
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            <Divider orientation="vertical" />
                                            <Tooltip title="Logout account">
                                                <IconButton
                                                    size="sm"
                                                    variant="soft"
                                                    color="danger"
                                                    onClick={() => onLogout(account.phone)}
                                                    loading={loggingOutPhone === account.phone}
                                                    disabled={loggingOutPhone === account.phone}
                                                >
                                                    <LogoutIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Stack>
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </Table>
    )
}
